<script lang="ts" setup>
import { TwiageCase } from "@/Case/models/TwiageCase";
import A11yDialog from "a11y-dialog";
import { onMounted, ref, watch } from "vue";
import Dialog from "./Dialog.vue";
import ProtocolDialog from "./ProtocolDialog.vue";
import { useProtocolStore } from "./store/protocol-store";
import { EmtLevel } from "./types/QuestionParams";

const props = defineProps<{ twiageCase: TwiageCase | null }>();

const protocolDialog = ref<InstanceType<typeof ProtocolDialog> | null>(null);

const protocolStore = useProtocolStore();

let emtLevelDialog: A11yDialog | undefined = undefined;

const emtLevelDialogRef = ref<{ getDialog: () => A11yDialog }>();

onMounted(() => {
  emtLevelDialog = emtLevelDialogRef.value?.getDialog();
});

watch(
  () => props.twiageCase,
  (newCase) => {
    protocolStore.setContext(newCase);
  },
  { immediate: true },
);

const show = () => {
  // Sets context and clears conversation if context changed
  // (e.g., opening from Start screen after being in a case will reset state)
  protocolStore.setContext(props.twiageCase);

  if (protocolStore.shouldShowEmtLevel.value && protocolStore.emsLevels.value.length > 0) {
    emtLevelDialog?.show();

    return;
  }

  protocolDialog.value?.show();
};

const handleNewQuestion = ({ question }: { question?: string }) => {
  return protocolStore.handleNewQuestion(question);
};

const handleEmsLevelSubmit = (event: Event) => {
  const data = new FormData(event.target as HTMLFormElement);
  const emsLevel = data.get("emsLevel") as EmtLevel;
  protocolStore.saveEmsLevel(emsLevel);
  emtLevelDialog?.hide();
  protocolDialog.value?.show();
};

const handleNewContext = () => {
  protocolStore.handleNewContext();
};
</script>

<template>
  <div class="protocol-ai-button">
    <div class="ai-btn" @click="show">
      <slot></slot>
    </div>

    <Dialog v-if="protocolStore.emsLevels.value.length > 0" ref="emtLevelDialogRef">
      <template #title>
        <h1 class="text-muted text-center">EMS Level</h1>
      </template>

      <template #description>
        <p class="text-muted text-left">Select your EMS Level to obtain the appropriate protocol information.</p>
      </template>

      <template #body>
        <form class="d-flex flex-column gap-2 align-items-start" @submit.prevent="handleEmsLevelSubmit">
          <div v-for="level in protocolStore.emsLevels.value" :key="level.id" class="form-check">
            <input :id="level.id" required class="form-check-input" type="radio" name="emsLevel" :value="level.id" />
            <label class="form-check-label text-black" :for="level.id"> {{ level.label }} </label>
          </div>
          <button class="btn background-red mt-5 col-12 border-0 btn-danger" type="submit">Save & Continue</button>
        </form>
      </template>
    </Dialog>
    <ProtocolDialog
      ref="protocolDialog"
      :asking-question="protocolStore.askingQuestion.value"
      :conversation="protocolStore.conversation.value"
      :twiage-case="props.twiageCase"
      @new-question="handleNewQuestion"
      @new-context="handleNewContext"
    />
  </div>
</template>

<style scoped>
.ai-btn > div > span {
  text-shadow: 0 0 10px rgba(255, 0, 34, 0.4);
  transition: all 0.3s ease;
  border: none;

  &:hover {
    text-shadow: 0 0 15px rgba(255, 0, 38, 0.6);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
    text-shadow: 0 0 8px rgba(255, 0, 34, 0.4);
  }
}
</style>
